// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { Appointment, Bundle, Parameters, Slot } from '@medplum/fhirtypes';
import { FHIR_BASE_PATH, FHIR_JSON_CONTENT_TYPE, SCHEDULING_OPERATION_NAMES } from './constants';
import type { SchedulingErrorContext } from './errors';
import { parseSchedulingError, SCHEDULING_ERROR_CATEGORIES, SchedulingError } from './errors';
import {
  buildBookParameters,
  buildFindUrl,
  buildHoldParameters,
  extractProposedAppointments,
  normalizeBookedAppointmentResponse,
  normalizeCancelledAppointmentResponse,
  validateConfirmAppointmentRequest,
} from './operations';
import { validateSchedulingScope, validateSchedulingTarget } from './tenant';
import type {
  AppointmentOperationRequest,
  BookAppointmentRequest,
  CancelAppointmentRequest,
  FindAppointmentRequest,
  SchedulingClient,
  SchedulingTransport,
  SchedulingTransportOptions,
} from './types';

function appointmentPath(operation: string, basePath: string, appointmentId?: string): string {
  const requiresAppointmentId =
    operation === SCHEDULING_OPERATION_NAMES.confirm || operation === SCHEDULING_OPERATION_NAMES.cancel;
  if (
    (requiresAppointmentId && (typeof appointmentId !== 'string' || !appointmentId.trim())) ||
    (!requiresAppointmentId &&
      appointmentId !== undefined &&
      (typeof appointmentId !== 'string' || !appointmentId.trim()))
  ) {
    throw new SchedulingError(SCHEDULING_ERROR_CATEGORIES.configurationInvalid, 'Appointment ID is required.');
  }
  const target = appointmentId
    ? `Appointment/${encodeURIComponent(appointmentId)}/${operation}`
    : `Appointment/${operation}`;
  return `${basePath}/${target}`;
}

export function createSchedulingClient(
  transport: SchedulingTransport,
  options: { basePath?: string } = {}
): SchedulingClient {
  const basePath = `/${(options.basePath ?? FHIR_BASE_PATH).replace(/^\/+|\/+$/g, '')}`;

  const run = async <T>(operation: () => Promise<T>, errorContext?: SchedulingErrorContext): Promise<T> => {
    try {
      return await operation();
    } catch (error) {
      throw parseSchedulingError(error, errorContext);
    }
  };

  const book = async (request: BookAppointmentRequest, mode: 'booked' | 'held', options?: SchedulingTransportOptions) =>
    run(
      async () => {
        const parameters = mode === 'booked' ? buildBookParameters(request) : buildHoldParameters(request);
        const response = await transport.post<Bundle<Appointment | Slot>>(
          appointmentPath(
            mode === 'booked' ? SCHEDULING_OPERATION_NAMES.book : SCHEDULING_OPERATION_NAMES.hold,
            basePath
          ),
          parameters,
          options
        );
        return normalizeBookedAppointmentResponse(response, mode, request.target, request.appointment);
      },
      {
        operation: mode === 'booked' ? SCHEDULING_OPERATION_NAMES.book : SCHEDULING_OPERATION_NAMES.hold,
        resourceType: 'Appointment',
      }
    );

  return {
    find: (request: FindAppointmentRequest, requestOptions?: SchedulingTransportOptions) =>
      run(
        async () => {
          const response = await transport.get<Parameters | Bundle>(buildFindUrl(request, basePath), requestOptions);
          return extractProposedAppointments(response, request.target);
        },
        { operation: SCHEDULING_OPERATION_NAMES.find }
      ),
    book: (request, requestOptions) => book(request, 'booked', requestOptions),
    hold: (request, requestOptions) => book(request, 'held', requestOptions),
    confirm: (request: AppointmentOperationRequest, requestOptions?: SchedulingTransportOptions) =>
      run(
        async () => {
          validateSchedulingTarget(request.target);
          validateConfirmAppointmentRequest(request.appointment, request.target);
          const response = await transport.post<Bundle<Appointment | Slot>>(
            appointmentPath(SCHEDULING_OPERATION_NAMES.confirm, basePath, request.appointment.id),
            undefined,
            requestOptions
          );
          return normalizeBookedAppointmentResponse(response, 'booked', request.target, request.appointment);
        },
        { operation: SCHEDULING_OPERATION_NAMES.confirm, resourceType: 'Appointment' }
      ),
    cancel: (request: CancelAppointmentRequest, requestOptions?: SchedulingTransportOptions) =>
      run(
        async () => {
          validateSchedulingScope(request.scope);
          return normalizeCancelledAppointmentResponse(
            await transport.post<Appointment | Parameters>(
              appointmentPath(SCHEDULING_OPERATION_NAMES.cancel, basePath, request.appointmentId),
              request.cancelationReason
                ? {
                    resourceType: 'Parameters',
                    parameter: [{ name: 'cancelationReason', valueCodeableConcept: request.cancelationReason }],
                  }
                : undefined,
              requestOptions
            ),
            request.appointmentId,
            request.scope
          );
        },
        { operation: SCHEDULING_OPERATION_NAMES.cancel, resourceType: 'Appointment' }
      ),
  };
}

export function createMedplumFetchTransport(fetchImpl: typeof fetch): SchedulingTransport {
  return {
    async get<T>(url: string, options?: SchedulingTransportOptions) {
      const response = await fetchImpl(url, { method: 'GET', signal: options?.signal });
      if (!response.ok) {
        throw await response.json();
      }
      return (await response.json()) as T;
    },
    async post<T>(url: string, body: unknown, options?: SchedulingTransportOptions) {
      const requestInit: RequestInit = {
        method: 'POST',
        signal: options?.signal,
      };
      if (body !== undefined) {
        requestInit.body = JSON.stringify(body);
        requestInit.headers = { 'Content-Type': FHIR_JSON_CONTENT_TYPE };
      }
      const response = await fetchImpl(url, requestInit);
      if (!response.ok) {
        throw await response.json();
      }
      return (await response.json()) as T;
    },
  };
}
